import _ from 'lodash';
import React, { useMemo } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import momentTimezone from 'moment-timezone';
import {
  Field as FormikField,
  withFormik,
} from 'formik';
import { useQuery } from '@apollo/client';
import { InputFormField as Field, withTranslation } from '@playbookux/playbook-web-core';

import {
  Container,
  Segment,
  Text,
  Title,
  Header,
  Form,
  BackButton,
  NextButton,
  ButtonContainer,
  ContainerDate,
  TextBold,
  ErrorMessage,
} from './styles';
import schema from '../schema';
import RescheduleModal from '../../../components/reschedule-modal';

import {
  TIMEZONE_NAME,
} from '../../../../core/components/timezone-dropdown/graphql';

const FORMAT_DATE_TIME = 'dddd, MMMM Do YYYY, h:mm:ss a';

const formatDateTime = (time, timeZone) => {
  if (!time) return null;

  const newTime = momentTimezone(time).tz(timeZone).format(FORMAT_DATE_TIME);
  return newTime;
};

const ScheduleForm = (props) => {
  const {
    date,
    device,
    timeZone,
    handleSubmit,
    loading,
    originalTestSession,
    open,
    onClose,
    setFieldValue,
    onBack,
    errors,
    touched,
    collectTesterName,
    phrases,
  } = props;

  // Used to get the remaped timezone name
  const {
    data: dataTimezoneName = {},
  } = useQuery(TIMEZONE_NAME, {
    variables: {
      unix: timeZone,
    },
    skip: !timeZone,
  });

  const { timezoneName = '' } = dataTimezoneName;

  const formatDate = formatDateTime(date, timeZone);

  const handleSubmitForm = (forceReschedule) => {
    setFieldValue('forceReschedule', forceReschedule);
    handleSubmit();
  };

  const errorsList = useMemo(() => {
    let list = [];
    if (Object.keys(errors).length > 0) {
      const keys = Object.keys(errors);
      list = keys.reduce((acc, key) => {
        if (touched[key]) {
          return `${acc} ${errors[key]} \n`;
        }
        return acc;
      }, '');
    }
    return list;
  }, [errors, touched]);

  return (
    <Container>
      <RescheduleModal
        open={open && !_.isEmpty(originalTestSession)}
        loading={loading}
        onClose={onClose}
        onSubmit={() => handleSubmitForm(true)}
        content={`It looks like your email is already signed up for a session with this project
          on ${formatDateTime(originalTestSession.startAt, timeZone)} ${timeZone}. Would you like to reschedule
          that session to the new time that you’ve just selected (${formatDate} ${timeZone})?
        `}
      />
      <Segment>
        <Title as="h1" aria-label={phrases.scheduler_confirm_title} tabIndex="0">{phrases.scheduler_confirm_title}</Title>
        <Header>
          <ContainerDate tabIndex="0">
            <TextBold>{phrases.scheduler_confirm_day}</TextBold>
            <Text>
              &nbsp;
              {`${formatDate}, ${timezoneName}`}
            </Text>
          </ContainerDate>
          <Text tabIndex="0">{phrases.scheduler_confirm_device.replace('%DEVICE', device)}</Text>
        </Header>
        <Form>
          <FormikField
            name="email"
            component={Field}
            placeholder="example@email.com"
            labelText={phrases.scheduler_confirm_email}
            labelFontSize="14px"
            ariaLabel="Enter your email address"
            ariaDescribedBy={errors.email && touched.email ? 'email-error' : null}
            ariaRequired="true"
            ariaInvalid={errors.email && touched.email ? 'true' : null}
            role="textbox"
          />
          {collectTesterName && (
            <>
              <FormikField
                name="firstName"
                component={Field}
                placeholder="First name"
                labelText={phrases.scheduler_confirm_first_name}
                labelFontSize="14px"
                ariaLabel="Enter your first name"
                ariaDescribedBy={errors.firstName && touched.firstName ? 'firstName-error' : null}
                ariaRequired="true"
                ariaInvalid={errors.firstName && touched.firstName ? 'true' : null}
                role="textbox"
              />
              <FormikField
                name="lastName"
                component={Field}
                placeholder="Last name"
                labelText={phrases.scheduler_confirm_first_name}
                labelFontSize="14px"
                ariaLabel="Enter your last name"
                ariaDescribedBy={errors.lastName && touched.lastName ? 'lastName-error' : null}
                ariaRequired="true"
                ariaInvalid={errors.lastName && touched.lastName ? 'true' : null}
                role="textbox"
              />
            </>
          )}
        </Form>
        <Text tabIndex="0">{phrases.scheduler_confirm_sent}</Text>
      </Segment>

      {errorsList.length > 0 && (
        <ErrorMessage
          tabIndex="0"
          role="alert"
          aria-live="polite"
          aria-label={errorsList}
        >
          {errorsList}
        </ErrorMessage>
      )}

      <ButtonContainer>
        <BackButton
          onClick={onBack}
          tabindex="0"
          aria-label="Go back to schedule page"
          role="button"
        >
          {phrases.scheduler_back_button}
        </BackButton>

        <NextButton
          onClick={() => handleSubmitForm(false)}
          loading={loading}
          disabled={loading || Object.keys(errors).length > 0}
          tabindex="0"
          aria-label={phrases.scheduler_confirm_button}
          role="button"
        >
          {phrases.scheduler_confirm_button}
        </NextButton>
      </ButtonContainer>
    </Container>
  );
};

ScheduleForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  originalTestSession: PropTypes.shape({
    startAt: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  date: PropTypes.string.isRequired,
  device: PropTypes.string.isRequired,
  timeZone: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }).isRequired,
  touched: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }).isRequired,
  collectTesterName: PropTypes.bool.isRequired,
  phrases: PropTypes.shape({
    scheduler_confirm_title: PropTypes.string,
    scheduler_confirm_day: PropTypes.string,
    scheduler_confirm_sent: PropTypes.string,
    scheduler_confirm_device: PropTypes.string,
    scheduler_confirm_email: PropTypes.string,
    scheduler_confirm_first_name: PropTypes.string,
    scheduler_confirm_last_name: PropTypes.string,
    scheduler_confirm_button: PropTypes.string,
    scheduler_back_button: PropTypes.string,
  }).isRequired,
};

export default compose(
  withTranslation,
  withFormik({
    validationSchema: schema,
    enableReinitialize: true,
    mapPropsToValues: (props) => ({
      email: '',
      firstName: '',
      lastName: '',
      collectTesterName: props.collectTesterName,
    }),
    handleSubmit: async (values, { props, setStatus }) => {
      try {
        const { onSubmit, timeZone } = props;
        await onSubmit({
          ...values,
          timeZone,
        });
      } catch (ex) {
        setStatus({ error: ex });
      }
    },
  }),
)(ScheduleForm);
